import React, { useState, useEffect } from 'react';
import xService from '../services/xService';
import XAnalysisDialog from './XAnalysisDialog';
import XCustomAnalysisDialog from './XCustomAnalysisDialog';

const XSearchResultsWithHistory = ({ searchId, onClose }) => {
    const [history, setHistory] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentResults, setCurrentResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showAnalysis, setShowAnalysis] = useState(false);
    const [showCustomAnalysis, setShowCustomAnalysis] = useState(false);
    const [navigatingWithResults, setNavigatingWithResults] = useState(false);

    useEffect(() => {
        loadHistory();
    }, [searchId]);

    const loadHistory = async () => {
        try {
            setLoading(true);
            setError('');

            // Get history of results for this search
            const historyData = await xService.getSearchHistory(searchId, 50);

            if (historyData.history && historyData.history.length > 0) {
                setHistory(historyData.history);
                // Load the most recent result
                await loadResultByIndex(0, historyData.history);
            } else {
                setError('No results found for this search.');
            }
        } catch (err) {
            console.error('Error loading history:', err);
            setError('Failed to load search history: ' + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    const loadResultByIndex = async (index, historyList = history) => {
        try {
            setLoading(true);
            const historyItem = historyList[index];

            // Load full result data (use 'id' field from DynamoDB metadata)
            const data = await xService.getResultById(historyItem.id);
            setCurrentResults(data.result);
            setCurrentIndex(index);
        } catch (err) {
            console.error('Error loading result:', err);
            setError('Failed to load result: ' + (err.response?.data?.detail || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handlePrevious = () => {
        if (currentIndex < history.length - 1) {
            loadResultByIndex(currentIndex + 1);
        }
    };

    const handleNext = () => {
        if (currentIndex > 0) {
            loadResultByIndex(currentIndex - 1);
        }
    };

    const findOlderWithResults = async () => {
        setNavigatingWithResults(true);
        try {
            // Start from the next older item
            for (let i = currentIndex + 1; i < history.length; i++) {
                const historyItem = history[i];
                const data = await xService.getResultById(historyItem.id);
                
                if (data.result && data.result.tweet_count > 0) {
                    // Found one with results!
                    setCurrentResults(data.result);
                    setCurrentIndex(i);
                    break;
                }
            }
        } catch (err) {
            console.error('Error finding older results:', err);
            setError('Failed to find older results: ' + (err.response?.data?.detail || err.message));
        } finally {
            setNavigatingWithResults(false);
        }
    };

    const findNewerWithResults = async () => {
        setNavigatingWithResults(true);
        try {
            // Start from the next newer item
            for (let i = currentIndex - 1; i >= 0; i--) {
                const historyItem = history[i];
                const data = await xService.getResultById(historyItem.id);
                
                if (data.result && data.result.tweet_count > 0) {
                    // Found one with results!
                    setCurrentResults(data.result);
                    setCurrentIndex(i);
                    break;
                }
            }
        } catch (err) {
            console.error('Error finding newer results:', err);
            setError('Failed to find newer results: ' + (err.response?.data?.detail || err.message));
        } finally {
            setNavigatingWithResults(false);
        }
    };

    const formatDate = (dateString) => {
        try {
            return new Date(dateString).toLocaleString();
        } catch {
            return dateString;
        }
    };

    if (!searchId) return null;

    return (
        <>
            <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-xl" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                    <div className="modal-content" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                    <div className="modal-header" style={{ flexShrink: 0 }}>
                        <h5 className="modal-title">
                            <i className="bi bi-twitter-x me-2"></i>
                            {currentResults ? `Results: ${currentResults.search_name}` : 'Loading...'}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                        ></button>
                    </div>

                    {/* History Navigation & Summary */}
                    {history.length > 0 && currentResults && (
                        <div className="modal-header border-top" style={{ flexShrink: 0 }}>
                            <div className="d-flex align-items-center justify-content-between w-100">
                                <div className="d-flex gap-2">
                                    {history.length > 1 && (
                                        <>
                                            <button
                                                className="btn btn-sm btn-outline-info"
                                                onClick={findOlderWithResults}
                                                disabled={navigatingWithResults || loading || currentIndex >= history.length - 1}
                                                title="Find older results with tweets"
                                            >
                                                {navigatingWithResults ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                        Searching...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-chevron-double-left me-1"></i>
                                                        Older with Results
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handlePrevious}
                                                disabled={loading || currentIndex >= history.length - 1}
                                            >
                                                <i className="bi bi-chevron-left"></i> Older
                                            </button>
                                        </>
                                    )}
                                </div>

                                <div className="text-center">
                                    <small className="text-muted">
                                        {history.length > 1 && (
                                            <>Result {currentIndex + 1} of {history.length} · </>
                                        )}
                                        Executed {formatDate(currentResults.executed_at)}
                                        {' · '}
                                        {currentResults.run_type && (
                                            <>
                                                <span className={`badge ${currentResults.run_type === 'scheduled' ? 'bg-success' : 'bg-info'}`}>
                                                    {currentResults.run_type === 'scheduled' ? (
                                                        <><i className="bi bi-clock-history me-1"></i>Scheduled</>
                                                    ) : (
                                                        <><i className="bi bi-hand-index me-1"></i>Manual</>
                                                    )}
                                                </span>
                                                {' · '}
                                            </>
                                        )}
                                        <strong>{currentResults.tweet_count}</strong> tweet{currentResults.tweet_count !== 1 ? 's' : ''}
                                        {currentResults.meta?.result_count !== undefined && 
                                         currentResults.meta.result_count !== currentResults.tweet_count && (
                                            <span className="text-warning">
                                                {' '}({currentResults.meta.result_count} found by X)
                                            </span>
                                        )}
                                    </small>
                                </div>

                                <div className="d-flex gap-2">
                                    {history.length > 1 && (
                                        <>
                                            <button
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={handleNext}
                                                disabled={loading || currentIndex === 0}
                                            >
                                                Newer <i className="bi bi-chevron-right"></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-info"
                                                onClick={findNewerWithResults}
                                                disabled={navigatingWithResults || loading || currentIndex === 0}
                                                title="Find newer results with tweets"
                                            >
                                                {navigatingWithResults ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                        Searching...
                                                    </>
                                                ) : (
                                                    <>
                                                        Newer with Results
                                                        <i className="bi bi-chevron-double-right ms-1"></i>
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="modal-body" style={{
                        flex: '1 1 auto',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        minHeight: 0
                    }}>
                        {loading && !currentResults && (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 text-muted">Loading results...</p>
                            </div>
                        )}

                        {error && (
                            <div className="alert alert-danger" role="alert">
                                <i className="bi bi-exclamation-circle me-2"></i>
                                {error}
                            </div>
                        )}

                        {currentResults && !loading && (
                            <>
                                {/* No results */}
                                {currentResults.tweet_count === 0 ? (
                                    <div className="text-center py-4">
                                        <i className="bi bi-inbox display-1 text-muted"></i>
                                        <h5 className="mt-3">No tweets found</h5>
                                        <p className="text-muted">This search returned no results at the time it was run</p>
                                    </div>
                                ) : (
                                    /* Tweets list */
                                    <div className="list-group">
                                        {renderTweets(currentResults)}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="modal-footer" style={{ flexShrink: 0 }}>
                        <div className="d-flex justify-content-between w-100">
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-primary me-2"
                                    onClick={() => setShowAnalysis(true)}
                                    disabled={!currentResults || !history[currentIndex]}
                                >
                                    <i className="bi bi-stars me-2"></i>
                                    Analyze Results
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => setShowCustomAnalysis(true)}
                                    disabled={!history || history.length === 0}
                                >
                                    <i className="bi bi-magic me-2"></i>
                                    Custom Analysis
                                </button>
                            </div>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        {/* Analysis Dialog */}
        {showAnalysis && currentResults && history[currentIndex] && (
            <XAnalysisDialog
                show={showAnalysis}
                onClose={() => setShowAnalysis(false)}
                resultId={history[currentIndex].id}
                searchName={currentResults.search_name}
            />
        )}

        {/* Custom Analysis Dialog */}
        {showCustomAnalysis && (
            <XCustomAnalysisDialog
                show={showCustomAnalysis}
                onClose={() => setShowCustomAnalysis(false)}
                searchId={searchId}
                searchName={currentResults?.search_name || 'Unknown Search'}
                availableResults={history}
            />
        )}
    </>
);

function renderTweets(results) {
        const { tweets, includes } = results;

        // Create a map of users for easy lookup
        const usersMap = {};
        if (includes?.users) {
            includes.users.forEach(user => {
                usersMap[user.id] = user;
            });
        }

        // Create a map of tweets (for retweets)
        const tweetsMap = {};
        if (includes?.tweets) {
            includes.tweets.forEach(tweet => {
                tweetsMap[tweet.id] = tweet;
            });
        }

        // Group tweets by original tweet ID (for retweets)
        const tweetGroups = new Map();

        tweets.forEach((tweet) => {
            const referencedRetweet = tweet.referenced_tweets?.find(ref => ref.type === 'retweeted');
            const isRetweetByText = tweet.text.startsWith('RT @');
            const isRetweet = isRetweetByText || !!referencedRetweet;

            let groupKey = tweet.id; // Default: use tweet's own ID

            if (isRetweet && referencedRetweet) {
                // Use the original tweet ID as the group key
                groupKey = referencedRetweet.id;
            } else if (isRetweet) {
                // For text-based RT detection without referenced_tweets, try to extract
                // Group by the tweet text to catch duplicates
                groupKey = `rt:${tweet.text}`;
            }

            if (!tweetGroups.has(groupKey)) {
                tweetGroups.set(groupKey, []);
            }
            tweetGroups.get(groupKey).push(tweet);
        });

        // Render grouped tweets
        return Array.from(tweetGroups.entries()).map(([groupKey, groupedTweets]) => {
            const isGrouped = groupedTweets.length > 1;
            const primaryTweet = groupedTweets[0]; // Use first tweet as primary

            // Collect all authors
            const authors = groupedTweets.map(t => usersMap[t.author_id]).filter(Boolean);

            // Check if this is a retweet
            const isRetweetByText = primaryTweet.text.startsWith('RT @');
            const referencedRetweet = primaryTweet.referenced_tweets?.find(ref => ref.type === 'retweeted');
            const isRetweet = isRetweetByText || !!referencedRetweet;

            let originalUsername = null;
            let originalTweetId = primaryTweet.id;

            if (referencedRetweet) {
                originalTweetId = referencedRetweet.id;
            }

            if (isRetweetByText) {
                const match = primaryTweet.text.match(/^RT @(\w+):/);
                if (match) {
                    originalUsername = match[1];
                }
            }

            const linkUsername = originalUsername || authors[0]?.username || 'i/web';

            // Calculate aggregated metrics
            const aggregatedMetrics = {
                like_count: 0,
                retweet_count: 0,
                reply_count: 0
            };

            groupedTweets.forEach(tweet => {
                if (tweet.public_metrics) {
                    aggregatedMetrics.like_count += tweet.public_metrics.like_count || 0;
                    aggregatedMetrics.retweet_count += tweet.public_metrics.retweet_count || 0;
                    aggregatedMetrics.reply_count += tweet.public_metrics.reply_count || 0;
                }
            });

            // Calculate time span
            const timestamps = groupedTweets
                .map(t => t.created_at ? new Date(t.created_at).getTime() : null)
                .filter(t => t !== null);

            const minTime = timestamps.length > 0 ? new Date(Math.min(...timestamps)) : null;
            const maxTime = timestamps.length > 0 ? new Date(Math.max(...timestamps)) : null;

            return (
                <div key={groupKey} className="list-group-item">
                    <div className="d-flex w-100 justify-content-between align-items-start">
                        <div className="flex-grow-1">
                            {/* Author info - show all retweeters if grouped */}
                            {authors.length > 0 && (
                                <div className="mb-2">
                                    {isGrouped ? (
                                        <>
                                            <span className="badge bg-info text-dark me-2">
                                                {groupedTweets.length} Retweets
                                            </span>
                                            <strong>
                                                {authors.map(a => a.name).join(', ')}
                                            </strong>
                                            <br />
                                            <small className="text-muted">
                                                @{authors.map(a => a.username).join(', @')}
                                            </small>
                                        </>
                                    ) : (
                                        <>
                                            <strong>{authors[0].name}</strong>
                                            <span className="text-muted ms-2">@{authors[0].username}</span>
                                            {authors[0].verified && (
                                                <i className="bi bi-patch-check-fill text-primary ms-1" title="Verified"></i>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Tweet text */}
                            <p className="mb-2">
                                {isRetweet && <span className="text-muted"><i className="bi bi-arrow-repeat"></i> Retweeted: </span>}
                                {primaryTweet.text}
                            </p>

                            {/* Metadata */}
                            <div className="small text-muted">
                                {/* Time span for grouped tweets */}
                                {isGrouped && minTime && maxTime ? (
                                    <span className="me-3">
                                        <i className="bi bi-clock me-1"></i>
                                        {minTime.getTime() === maxTime.getTime()
                                            ? formatDate(minTime.toISOString())
                                            : `${formatDate(minTime.toISOString())} - ${formatDate(maxTime.toISOString())}`
                                        }
                                    </span>
                                ) : (
                                    <span className="me-3">
                                        <i className="bi bi-clock me-1"></i>
                                        {primaryTweet.created_at ? formatDate(primaryTweet.created_at) : 'Unknown'}
                                    </span>
                                )}

                                {/* Aggregated metrics */}
                                {(aggregatedMetrics.like_count > 0 || aggregatedMetrics.retweet_count > 0 || aggregatedMetrics.reply_count > 0) && (
                                    <>
                                        <span className="me-3">
                                            <i className="bi bi-heart me-1"></i>
                                            {aggregatedMetrics.like_count}
                                        </span>
                                        <span className="me-3">
                                            <i className="bi bi-arrow-repeat me-1"></i>
                                            {aggregatedMetrics.retweet_count}
                                        </span>
                                        <span className="me-3">
                                            <i className="bi bi-chat me-1"></i>
                                            {aggregatedMetrics.reply_count}
                                        </span>
                                    </>
                                )}

                                {primaryTweet.lang && (
                                    <span className="badge bg-secondary">
                                        {primaryTweet.lang}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* View on X button */}
                        <a
                            href={`https://x.com/${linkUsername}/status/${originalTweetId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary ms-3"
                            title="View on X"
                        >
                            <i className="bi bi-box-arrow-up-right"></i>
                        </a>
                    </div>
                </div>
            );
        });
    }
};

export default XSearchResultsWithHistory;
